import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { Typography } from "interfaces/typography";
import { useUserData } from '../../../lib/redux/users/user-data-hook';
import { Search, Users, User, UserCircle } from "lucide-react";
import { useAutodialerSettings } from "../hooks/useAutodialerSettings";
import { SelectionType } from "interfaces/settings";

export function RepTeamSelector() {
    const { selection, updateSelection } = useAutodialerSettings();
    const { sortedUserGroupInfo: userGroupInfo, userRaw: currentUser, allVisibleUsers, isAdmin } = useUserData();
    const adminUserGroups = useSelector((state: RootState) => state.adminWrite.value ? state.adminWrite.value.user_group_ids : []);
    const [searchFilter, setSearchFilter] = useState<string>('');
    const filteredUsers = useMemo(() => {
        return allVisibleUsers.filter(u => u.user_id !== currentUser?.user_id && (!searchFilter || u.user_name.toLowerCase().includes(searchFilter.toLowerCase())))
    }, [allVisibleUsers, searchFilter, currentUser])

    const userGroups = useMemo(() => {
        return userGroupInfo?.filter((v) => adminUserGroups.includes(v.group.user_group_id) && (!searchFilter || v.group.user_group_name.toLowerCase().includes(searchFilter.toLowerCase()))) || []
    }, [userGroupInfo, adminUserGroups, searchFilter])

    // Group users by subteams
    const { usersBySubteam, unassignedUsers } = useMemo(() => {
        const usersBySubteam = new Map<string, typeof filteredUsers>();
        const unassignedUsers: typeof filteredUsers = [];

        filteredUsers.forEach(user => {
            const userSubteam = userGroups.find(group => 
                group.allUserIds.includes(user.user_id)
            );
            
            if (userSubteam) {
                const subteamId = userSubteam.group.user_group_id;
                if (!usersBySubteam.has(subteamId)) {
                    usersBySubteam.set(subteamId, []);
                }
                usersBySubteam.get(subteamId)!.push(user);
            } else {
                unassignedUsers.push(user);
            }
        });

        return { usersBySubteam, unassignedUsers };
    }, [filteredUsers, userGroups])
    
    if (!isAdmin) return null
    
    return (
        <div className="flex flex-col h-full">
            {/* Section Header */}
            <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0">
                <Typography 
                    variant="smallParagraph" 
                    color="#6B7280" 
                    fontWeight="600"
                    style={{ 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.5px',
                        fontSize: '11px'
                    }}
                >
                    Configure For
                </Typography>
            </div>
            
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {/* Search Filter */}
                <div>
                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                {/* Selection Options */}
                <div className="space-y-0.5">
                    {/* Me (Personal Settings) */}
                    <button
                        className={`w-full text-left px-2 py-1.5 text-xs rounded-md transition-colors flex items-center min-h-[28px] ${
                            selection?.type === 'me' 
                                ? 'bg-purple-500 text-white' 
                                : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        onClick={() => updateSelection(SelectionType.ME)}
                    >
                        <UserCircle className="w-3 h-3 mr-1.5 flex-shrink-0" />
                        <Typography 
                            variant="smallParagraph" 
                            color={selection?.type === 'me' ? 'white' : 'black'}
                            style={{ fontSize: '12px', wordBreak: 'break-word', lineHeight: '1.2' }}
                            fontWeight="600"
                        >
                            Me
                        </Typography>
                    </button>

                    {/* Admin Options - only show if user is admin */}
                            <div className="border-t border-gray-200 my-1.5"></div>
                            
                            {/* Overall Team */}
                            <button
                                className={`w-full text-left px-2 py-1.5 text-xs rounded-md transition-colors flex items-center min-h-[28px] ${
                                    selection?.type === 'overall_team' 
                                        ? 'bg-blue-500 text-white' 
                                        : 'text-gray-700 hover:bg-gray-100'
                                }`}
                                onClick={() => updateSelection(SelectionType.OVERALL_TEAM)}
                            >
                                <Users className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                <Typography 
                                    variant="smallParagraph" 
                                    color={selection?.type === 'overall_team' ? 'white' : 'black'}
                                    style={{ fontSize: '12px', wordBreak: 'break-word', lineHeight: '1.2' }}
                                >
                                    Overall Team
                                </Typography>
                            </button>
                            
                            {/* Sub Teams - only show if there are any */}
                            {userGroups.length > 0 && (
                                <>
                                    <div className="border-t border-gray-200 my-1.5"></div>
                                    {userGroups.map((group) => (
                                        <button
                                            key={group.group.user_group_id}
                                            className={`w-full text-left px-2 py-1.5 text-xs rounded-md transition-colors flex items-center min-h-[28px] ${
                                                selection?.type === 'sub_team' && selection?.id === group.group.user_group_id 
                                                    ? 'bg-blue-500 text-white' 
                                                    : 'text-gray-700 hover:bg-gray-100'
                                            }`}
                                            onClick={() => updateSelection(SelectionType.SUB_TEAM, group.group.user_group_id)}
                                        >
                                            <Users className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                            <Typography 
                                                variant="smallParagraph" 
                                                color={selection?.type === 'sub_team' && selection?.id === group.group.user_group_id ? 'white' : 'black'}
                                                style={{ fontSize: '12px', wordBreak: 'break-word', lineHeight: '1.2' }}
                                            >
                                                {group.group.user_group_name}
                                            </Typography>
                                        </button>
                                    ))}
                                </>
                            )}
                            
                            {/* Individual Users - organized by subteams if subteams exist, otherwise flat list */}
                            {(filteredUsers.length > 0 || usersBySubteam.size > 0) && (
                                <>
                                    <div className="border-t border-gray-200 my-1.5"></div>
                                    
                                    {/* If there are subteams, show users organized by subteams */}
                                    {userGroups.length > 0 ? (
                                        <>
                                            {/* Users organized by subteams */}
                                            {userGroups.map((group) => {
                                                const subteamUsers = usersBySubteam.get(group.group.user_group_id) || [];
                                                if (subteamUsers.length === 0) return null;
                                                
                                                return (
                                                    <div key={`subteam-users-${group.group.user_group_id}`} className="mb-2">
                                                        <div className="px-2 py-1 text-xs text-gray-500 font-medium">
                                                            <Typography variant="smallParagraph" color="#6B7280" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }} className="truncate">
                                                                {group.group.user_group_name}
                                                            </Typography>
                                                        </div>
                                                        {subteamUsers.map((visibleUser) => (
                                                            <button
                                                                key={visibleUser.user_id}
                                                                className={`w-full text-left px-4 py-1.5 text-xs rounded-md transition-colors flex items-center min-h-[28px] ${
                                                                    selection?.type === SelectionType.USER && selection?.id === visibleUser.user_id 
                                                                        ? 'bg-green-500 text-white' 
                                                                        : 'text-gray-700 hover:bg-gray-100'
                                                                }`}
                                                                onClick={() => updateSelection(SelectionType.USER, visibleUser.user_id)}
                                                            >
                                                                <User className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                                                <Typography 
                                                                    variant="smallParagraph" 
                                                                    color={selection?.type === SelectionType.USER && selection?.id === visibleUser.user_id ? 'white' : 'black'}
                                                                    style={{ fontSize: '12px', wordBreak: 'break-word', lineHeight: '1.2' }}
                                                                    fontWeight="500"
                                                                >
                                                                    {visibleUser.user_name}
                                                                </Typography>
                                                            </button>
                                                        ))}
                                                    </div>
                                                );
                                            })}
                                            
                                            {/* Unassigned users */}
                                            {unassignedUsers.length > 0 && (
                                                <div className="mb-2">
                                                    <div className="px-2 py-1 text-xs text-gray-500 font-medium">
                                                        <Typography variant="smallParagraph" color="#6B7280" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }} className="truncate">
                                                            Unassigned
                                                        </Typography>
                                                    </div>
                                                    {unassignedUsers.map((visibleUser) => (
                                                        <button
                                                            key={visibleUser.user_id}
                                                            className={`w-full text-left px-4 py-1.5 text-xs rounded-md transition-colors flex items-center min-h-[28px] ${
                                                                selection?.type === SelectionType.USER && selection?.id === visibleUser.user_id 
                                                                    ? 'bg-green-500 text-white' 
                                                                    : 'text-gray-700 hover:bg-gray-100'
                                                            }`}
                                                            onClick={() => updateSelection(SelectionType.USER, visibleUser.user_id)}
                                                        >
                                                            <User className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                                            <Typography 
                                                                variant="smallParagraph" 
                                                                color={selection?.type === SelectionType.USER && selection?.id === visibleUser.user_id ? 'white' : 'black'}
                                                                style={{ fontSize: '12px', wordBreak: 'break-word', lineHeight: '1.2' }}
                                                                fontWeight="500"
                                                            >
                                                                {visibleUser.user_name}
                                                            </Typography>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        /* Flat list if no subteams */
                                        filteredUsers.map((visibleUser) => (
                                            <button
                                                key={visibleUser.user_id}
                                                className={`w-full text-left px-2 py-1.5 text-xs rounded-md transition-colors flex items-center min-h-[28px] ${
                                                    selection?.type === SelectionType.USER && selection?.id === visibleUser.user_id 
                                                        ? 'bg-green-500 text-white' 
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                                onClick={() => updateSelection(SelectionType.USER, visibleUser.user_id)}
                                            >
                                                <User className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                                <Typography 
                                                    variant="smallParagraph" 
                                                    color={selection?.type === SelectionType.USER && selection?.id === visibleUser.user_id ? 'white' : 'black'}
                                                    style={{ fontSize: '12px', wordBreak: 'break-word', lineHeight: '1.2' }}
                                                    fontWeight="500"
                                                >
                                                    {visibleUser.user_name}
                                                </Typography>
                                            </button>
                                        ))
                                    )}
                                </>
                            )}
                </div>
                
                {/* No results message */}
                {searchFilter && userGroups.length === 0 && filteredUsers.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-gray-500 italic">
                        <Typography variant="smallParagraph" color="#6B7280" style={{ fontSize: '11px' }}>
                            No results found for "{searchFilter}"
                        </Typography>
                    </div>
                )}
            </div>
        </div>
    );
} 